import { FacetDetail } from "@/types/productListing";
import { CheckboxFacet } from "./facetTypes/CheckboxFacet/CheckboxFacet";
import { ColourFacet } from "./facetTypes/ColourFacet/ColourFacet";
import { TextFacet } from "./facetTypes/TextFacet/TextFacet";
import { ToggleFacet } from "./facetTypes/ToggleFacet/ToggleFacet";
import styles from "./FacetItem.module.scss";
import classNames from "classnames/bind";
import { RouteParams, useRouteParams } from "@/hooks/useRouteParams";
import { sanitiseQueryParams } from "@/utils/queryUtils";
import { FILTER_APPLIED, FILTER_REMOVED } from "@/utils/events";
import { useCallback } from "react";
import { LinkWithoutPrefetch } from "@/components/__shared__/LinkWithoutPrefetch/LinkWithoutPrefetch";
import { useRouter } from "next/router";

const cx = classNames.bind(styles);

function getFacetItem(
  dimensionName: string,
): [React.ComponentType<FacetComponentProps>, string] {
  switch (dimensionName) {
    case "Store.Department":
      return [TextFacet, "link"];
    case "skus.basicColor":
    case "childSKUs.basicColor":
      return [ColourFacet, "link"];
    case "childSKUs.hideOutOfStock":
    case "outOfStock":
      return [ToggleFacet, "switch"];
    default:
      return [CheckboxFacet, "checkbox"];
  }
}

export type FacetComponentProps = {
  facetDetails: FacetDetail;
  facetName: string;
  disabled?: boolean;
};

type FacetItemProps = FacetComponentProps & {
  dimensionName: string;
  setFacetSearchValue?: (value: string) => void;
  dispatchFacetAnalytics: (
    eventType: string,
    facetName: string,
    facetDetails: FacetDetail,
  ) => void;
  className?: string;
};

export const FacetItem = ({
  facetDetails,
  facetName,
  dimensionName,
  disabled = false,
  setFacetSearchValue,
  dispatchFacetAnalytics,
  className,
}: FacetItemProps & { dimensionName: string }) => {
  const route = useRouteParams();
  const router = useRouter();

  const [Component, role] = getFacetItem(dimensionName);

  const { query, pathname } = buildFacetLinkProps(
    route,
    facetDetails,
    dimensionName,
  );

  const scrollToTop = useCallback(() => {
    const body = document.getElementById("main-content-slot");
    if (body) {
      const viewportRelativeToGrid = body.getBoundingClientRect();
      const gridBelowCurrentViewport = viewportRelativeToGrid.top > 0;

      if (gridBelowCurrentViewport) {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        body.scrollIntoView({ behavior: "smooth" });
      }
    }
    router.events.off("routeChangeComplete", scrollToTop);
  }, []);

  const handleClick = useCallback(() => {
    router.events.on("routeChangeComplete", scrollToTop);
    dispatchFacetAnalytics(
      facetDetails.isSelected === "true" ? FILTER_REMOVED : FILTER_APPLIED,
      facetName,
      facetDetails,
    );
    setFacetSearchValue?.("");
  }, [facetDetails]);

  const isSelected = facetDetails.isSelected === "true";

  return (
    <LinkWithoutPrefetch
      href={{ query: sanitiseQueryParams(query), pathname }}
      className={cx("facetLink", { active: isSelected }, className)}
      data-testid={facetDetails.label}
      role={role || "link"}
      aria-checked={
        ["checkbox", "switch"].includes(role) ? isSelected : undefined
      }
      data-checked={["link"].includes(role) ? isSelected : undefined}
      onClick={handleClick}
      scroll={false}
    >
      <Component
        facetDetails={facetDetails}
        facetName={facetName}
        disabled={disabled}
      />
    </LinkWithoutPrefetch>
  );
};

function buildFacetLinkProps(
  route: RouteParams,
  facetDetails: FacetDetail,
  dimensionName: string,
) {
  const { queryWithoutPageOrChunk: query } = route;
  const updatedQuery = { ...query };

  const [facetUrlPath, facetParamString] =
    // if is price facet, will include query params
    facetDetails.facetUrl?.split("?", 2) || [];

  if (dimensionName === "price") {
    if (facetParamString) {
      facetParamString?.split("&").forEach((param) => {
        const [key, value] = param.split("=");
        if (key) {
          updatedQuery[key] = value;
        }
      });
    } else {
      delete updatedQuery.price;
      delete updatedQuery.priceBands;
    }
  }

  return {
    pathname: facetUrlPath,
    query: updatedQuery,
  };
}
